import type { PaymentType, SearchCategory, SearchType } from '../../enums';
import type { OfferCharges, PointsConversion } from '../hotelsUtils/types';
import { ComponentType } from '../../enums';
import { ActionType } from '../../enums/actionType';
import {
  calculateId,
  calculateTotalNights,
  calculateTotalPointsValueAnyPayWith,
  formatDate,
  getTotalPrice,
} from '../hotelsUtils/hotelsUtils';

export interface CreateViewItemListEventPayloadOptions {
  adults: number;
  availableProperties?: number | null;
  availableRooms?: number;
  category: SearchCategory;
  checkIn: string | Date;
  checkOut: string | Date;
  children: number;
  currency: string;
  infants: number;
  listName: string;
  location: string;
  payWith: PaymentType;
  pointsConversion: PointsConversion;
  quantity: number | null;
  results: {
    offer: {
      charges: OfferCharges;
    };
    property: {
      category: string | null;
      ctaMessage?: string | null;
      ctaMessageCategory?: string | null;
      hasOffer: boolean;
      id: string | null;
      name: string | null;
    };
    roomType: {
      name: string;
    };
  }[];
  type: SearchType;
}

export interface CreateViewItemListEventPayloadReturn {
  ecommerce: {
    currency: string;
    item_list_id: string;
    item_list_name: string;
    items: {
      cta_message?: string | null;
      cta_message_category?: string | null;
      has_offer: boolean;
      index: number;
      item_category: string | null;
      item_id: string | null;
      item_name: string | null;
      item_variant: string;
      points_value: number;
      price: number;
      quantity: number;
      strikethrough_price: boolean;
    }[];
  };
  event_data: {
    action: ActionType.VIEW;
    available_property_count: number | null;
    available_room_count: number | null;
    component_type: ComponentType.ITEM_LIST;
    end?: string;
    search_category: SearchCategory;
    search_payment_toggle: PaymentType;
    search_term: string;
    search_type: SearchType;
    start_date?: string;
    travellers_adult: number;
    travellers_children: number;
    travellers_infant: number;
  };
}

export const createViewItemListEventPayload = ({
  listName,
  location,
  currency,
  type,
  category,
  payWith,
  availableProperties,
  availableRooms,
  checkIn,
  checkOut,
  adults,
  children,
  infants,
  results,
  pointsConversion,
}: CreateViewItemListEventPayloadOptions): CreateViewItemListEventPayloadReturn => {
  const numberOfNights = calculateTotalNights(checkIn, checkOut);

  return {
    event_data: {
      action: ActionType.VIEW,
      component_type: ComponentType.ITEM_LIST,
      search_term: location,
      search_type: type,
      search_category: category,
      search_payment_toggle: payWith,
      available_property_count: availableProperties ?? null,
      available_room_count: availableRooms ?? null,
      ...(checkIn && {
        start_date: formatDate(checkIn),
      }),
      ...(checkOut && { end_date: formatDate(checkOut) }),
      travellers_adult: adults,
      travellers_children: children,
      travellers_infant: infants,
    },
    ecommerce: {
      currency,
      item_list_id: calculateId(listName),
      item_list_name: listName,
      items: results.map(({ property, roomType, offer }, index) => {
        const pointsValue = calculateTotalPointsValueAnyPayWith(offer.charges, pointsConversion);
        const cashValue = getTotalPrice(offer.charges);
        const hasStrikethroughPrice = Number(offer.charges.strikethrough?.price.amount) > 0;
        return {
          item_id: property.id,
          item_name: property.name,
          item_variant: roomType.name,
          item_category: property.category,
          index,
          quantity: numberOfNights,
          price: cashValue,
          points_value: pointsValue,
          ...(property.ctaMessage && { cta_message: property.ctaMessage }),
          ...(property.ctaMessageCategory && { cta_message_category: property.ctaMessageCategory }),
          has_offer: property.hasOffer,
          strikethrough_price: hasStrikethroughPrice,
        };
      }),
    },
  };
};
